// current weather conditions display
import STATUS from './status.mjs';
import { DateTime } from '../vendor/auto/luxon.mjs';
import { safeText } from './utils/fetch.mjs';
import WeatherDisplay from './weatherdisplay.mjs';
import { registerDisplay, timeZone } from './navigation.mjs';
import * as utils from './radar-utils.mjs';
import setTiles from './radar-tiles.mjs';
import processRadar from './radar-processor.mjs';
import {
	getRadarFinalSize, getRadarSourceSize, RADAR_SOURCE_SIZE, MAP_PER_DOPPLER_ROW,
} from './radar-constants.mjs';

// store processed radar as dataURLs to avoid re-processing frames as they slide backwards in time
// this is cleared upon changing the location displayed
// YEETLAB: each entry also keeps the raw downloaded blob and the height it was
// rendered at, so a rotate re-renders locally instead of refetching six frames
let processedRadars = [];

const RADAR_HOST = 'mesonet.agron.iastate.edu';

// YEETLAB: the radar host speaks HTTP/1.1, so a phone opening many connections to it
// stalls them all. Frames are fetched two at a time.
const RADAR_MAX_DOWNLOADS = 2;

// the newest frames may not be on the map service yet (see radar-processor.mjs)
const RADAR_FRESH_FRAMES = 2;

// run fn over the items with at most `limit` running at once; results keep the input order
// and a failure becomes null instead of rejecting the lot
const mapLimit = async (items, limit, fn) => {
	const results = new Array(items.length).fill(null);
	let next = 0;
	const worker = async () => {
		while (next < items.length) {
			const index = next;
			next += 1;
			try {
				// the frames have to be fetched in turn, not all at once
				// eslint-disable-next-line no-await-in-loop
				results[index] = await fn(items[index], index);
			} catch (_error) {
				results[index] = null;
			}
		}
	};
	await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
	return results;
};

// a frame that failed repeats its nearest good neighbour (the earlier one on a tie),
// so there are still one entry per frame, in time order, with the time that matches the picture
const fillFailedFrames = (frames) => frames.map((frame, index) => {
	if (frame) return frame;
	for (let distance = 1; distance < frames.length; distance += 1) {
		const before = frames[index - distance];
		if (before) return before;
		const after = frames[index + distance];
		if (after) return after;
	}
	return null;
});
class Radar extends WeatherDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Local Radar');

		this.okToDrawCurrentConditions = false;
		this.okToDrawCurrentDateTime = false;

		// set max images
		this.dopplerRadarImageMax = 6;
		// update timing
		this.timing.baseDelay = 350;
		this.timing.delay = [
			{ time: 4, si: 5 },
			{ time: 1, si: 0 },
			{ time: 1, si: 1 },
			{ time: 1, si: 2 },
			{ time: 1, si: 3 },
			{ time: 1, si: 4 },
			{ time: 4, si: 5 },
			{ time: 1, si: 0 },
			{ time: 1, si: 1 },
			{ time: 1, si: 2 },
			{ time: 1, si: 3 },
			{ time: 1, si: 4 },
			{ time: 4, si: 5 },
			{ time: 1, si: 0 },
			{ time: 1, si: 1 },
			{ time: 1, si: 2 },
			{ time: 1, si: 3 },
			{ time: 1, si: 4 },
			{ time: 12, si: 5 },
		];

		// YEETLAB: the radar is drawn to fit the stage it is in, and the stage
		// changes on rotate or when the parent dock collapses. Re-render from the
		// cached frames when the target height actually moves.
		this.renderedHeight = 0;
		this.rendering = false;
		let resizeTimer;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => this.resizeRadar(), 250);
		});
	}

	showCanvas(navCmd) {
		super.showCanvas(navCmd);
		// the parent shell may have sized the stage after the last render
		this.resizeRadar();
	}

	async resizeRadar() {
		if (!this.radarUrls?.length || this.rendering) return;
		if (Math.abs(getRadarFinalSize().height - this.renderedHeight) < 24) return;
		await this.renderRadar();
		if (this.active) this.drawCanvas();
	}

	async getData(weatherParameters, refresh) {
		if (!super.getData(weatherParameters, refresh)) return;

		// ALASKA AND HAWAII AREN'T SUPPORTED!
		if (this.weatherParameters.state === 'AK' || this.weatherParameters.state === 'HI') {
			this.setStatus(STATUS.noData);
			return;
		}

		const baseUrl = `https://${RADAR_HOST}/archive/data/`;
		const baseUrlEnd = '/GIS/uscomp/?F=0&P=n0r*.png'; // This URL returns an index of .png files for the given date

		// Always get today's data
		const today = DateTime.utc().startOf('day');
		const todayStr = today.toFormat('yyyy/LL/dd');
		const yesterday = today.minus({ days: 1 });
		const yesterdayStr = yesterday.toFormat('yyyy/LL/dd');
		const todayUrl = `${baseUrl}${todayStr}${baseUrlEnd}`;

		// Get today's data, then we'll see if we need yesterday's
		const todayList = await safeText(todayUrl);

		// Count available images from today
		let todayImageCount = 0;
		if (todayList) {
			const parser = new DOMParser();
			const xmlDoc = parser.parseFromString(todayList, 'text/html');
			const anchors = xmlDoc.querySelectorAll('a');
			todayImageCount = Array.from(anchors).filter((elem) => elem.innerHTML?.match(/n0r_\d{12}\.png/)).length;
		}

		// Only fetch yesterday's data if we don't have enough images from today
		// or if it's very early in the day when recent images might still be from yesterday
		const currentTimeUTC = DateTime.utc();
		const minutesSinceMidnight = currentTimeUTC.hour * 60 + currentTimeUTC.minute;
		const requiredTimeWindow = this.dopplerRadarImageMax * 5; // 5 minutes per image
		const needYesterday = todayImageCount < this.dopplerRadarImageMax || minutesSinceMidnight < requiredTimeWindow;

		// Build the final lists array
		const lists = [];
		if (needYesterday) {
			const yesterdayUrl = `${baseUrl}${yesterdayStr}${baseUrlEnd}`;
			const yesterdayList = await safeText(yesterdayUrl);
			if (yesterdayList) {
				lists.push(yesterdayList); // Add yesterday's data first
			}
		}
		if (todayList) {
			lists.push(todayList); // Add today's data
		}

		// convert to an array of png urls
		const pngs = lists.flatMap((html, htmlIdx) => {
			const parser = new DOMParser();
			const xmlDoc = parser.parseFromString(html, 'text/html');
			// add the base url - reconstruct the URL for each list
			const base = xmlDoc.createElement('base');
			if (htmlIdx === 0 && needYesterday) {
				// First item is yesterday's data when we fetched it
				base.href = `${baseUrl}${yesterdayStr}${baseUrlEnd}`;
			} else {
				// This is today's data (or the only data if yesterday wasn't fetched)
				base.href = `${baseUrl}${todayStr}${baseUrlEnd}`;
			}
			xmlDoc.head.append(base);
			const anchors = xmlDoc.querySelectorAll('a');
			const urls = [];
			Array.from(anchors).forEach((elem) => {
				if (elem.innerHTML?.match(/n0r_\d{12}\.png/)) {
					urls.push(elem.href);
				}
			});
			return urls;
		});

		// get the last few images
		const timestampRegex = /_(\d{12})\.png/;
		const sortedPngs = pngs.sort((a, b) => (a.match(timestampRegex)[1] < b.match(timestampRegex)[1] ? -1 : 1));
		const urls = sortedPngs.slice(-(this.dopplerRadarImageMax));

		// calculate offsets and sizes
		const offsetX = 120 * 2;
		const offsetY = 69 * 2;
		this.sourceXY = utils.getXYFromLatitudeLongitudeMap(this.weatherParameters);
		this.radarSourceXY = utils.getXYFromLatitudeLongitudeDoppler(this.weatherParameters, offsetX, offsetY);
		this.radarUrls = urls;

		await this.renderRadar();
	}

	// YEETLAB: split out of getData so a stage size change can redraw the frames
	// at the new height without re-fetching the image list or the images
	async renderRadar() {
		const { radarUrls: urls } = this;
		if (!urls?.length) return;

		// the radar fills the stage it is being drawn into, and the source crop
		// grows with it, so more map is shown rather than the same map zoomed in
		const finalSize = getRadarFinalSize();
		const sourceSize = getRadarSourceSize(finalSize);

		// grow the crop around the location rather than only downward, so the
		// selected city stays centred as the radar gets taller
		const extraRows = Math.round((sourceSize.height - RADAR_SOURCE_SIZE.height) / 2);
		const sourceXY = {
			x: this.sourceXY.x,
			y: Math.max(0, this.sourceXY.y - Math.round(extraRows * MAP_PER_DOPPLER_ROW)),
		};
		const radarSourceXY = {
			x: this.radarSourceXY.x,
			y: Math.max(0, this.radarSourceXY.y - extraRows * 2),
		};

		// set up the base map and overlay tiles
		setTiles({
			sourceXY,
			elemId: this.elemId,
			finalSize,
		});

		const radarKey = `${radarSourceXY.x.toFixed(0)}-${radarSourceXY.y.toFixed(0)}`;

		// reset the "used" flag on pre-processed radars
		// items that were not used during this process are deleted (either expired via time or change of location)
		processedRadars.forEach((radar) => { radar.used = false; });

		this.rendering = true;
		try {
			const frames = await mapLimit(urls, RADAR_MAX_DOWNLOADS, async (url, index) => {
				// store the time
				const timeMatch = url.match(/_(\d{4})(\d\d)(\d\d)(\d\d)(\d\d)\./);
				const [, year, month, day, hour, minute] = timeMatch;

				const radarKeyedTimestamp = `${radarKey}:${year}${month}${day}${hour}${minute}`;

				// check for a pre-processed radar
				const preProcessed = processedRadars.find((radar) => radar.key === radarKeyedTimestamp);
				// (marked first so a frame that fails to re-render is not thrown away)
				if (preProcessed) preProcessed.used = true;

				// use the pre-processed radar, or get a new one
				// YEETLAB: a cached frame is only reusable at the height it was rendered
				// at; otherwise re-render it from the cached blob, which needs no network
				// (a stored crop is only valid for the window it was fetched for; processRadar checks that)
				let processedRadar = preProcessed?.dataURL;
				if (!preProcessed || preProcessed.height !== finalSize.height) {
					const result = await processRadar({
						url,
						RADAR_HOST,
						OVERRIDES,
						radarSourceXY,
						finalSize,
						sourceSize,
						sourceBlob: preProcessed?.blob,
						sourceBlobKey: preProcessed?.blobKey,
						fresh: index >= urls.length - RADAR_FRESH_FRAMES,
					});
					processedRadar = result.dataURL;

					if (preProcessed) {
						preProcessed.dataURL = result.dataURL;
						preProcessed.blob = result.blob;
						preProcessed.blobKey = result.blobKey;
						preProcessed.height = finalSize.height;
					} else {
						processedRadars.push({
							key: radarKeyedTimestamp,
							dataURL: result.dataURL,
							blob: result.blob,
							blobKey: result.blobKey,
							height: finalSize.height,
							used: true,
						});
					}
				}

				const time = DateTime.fromObject({
					year,
					month,
					day,
					hour,
					minute,
				}, {
					zone: 'UTC',
				}).setZone(timeZone());

				return {
					time,
					dataURL: processedRadar,
				};
			});

			// YEETLAB: one frame failing must not take the whole radar down
			const filled = fillFailedFrames(frames);
			if (filled.some((frame) => !frame)) throw new Error('Unable to load any radar frame');
			// each entry needs its own element, a repeated frame included
			const radarInfo = filled.map(({ time, dataURL }) => ({
				time,
				elem: this.fillTemplate('frame', { map: { type: 'img', src: dataURL } }),
			}));

			// put the elements in the container
			const scrollArea = this.elem.querySelector('.scroll-area');
			scrollArea.innerHTML = '';
			scrollArea.append(...radarInfo.map((r) => r.elem));

			// set max length
			this.timing.totalScreens = radarInfo.length;

			this.times = radarInfo.map((radar) => radar.time);
			this.setStatus(STATUS.loaded);

			// YEETLAB: the visible band has to match what was just rendered
			this.elem.style.setProperty('--yc-radar-height', `${finalSize.height}px`);
			this.renderedHeight = finalSize.height;

			// clean up any unused stored radars
			processedRadars = processedRadars.filter((radar) => radar.used);
		} catch (_error) {
			// Radar fetch failed - skip this display in animation by setting totalScreens = 0
			this.timing.totalScreens = 0;
			if (this.isEnabled) this.setStatus(STATUS.failed);
		} finally {
			this.rendering = false;
		}
	}

	async drawCanvas() {
		super.drawCanvas();
		const time = this.times[this.screenIndex].toLocaleString(DateTime.TIME_SIMPLE);
		const timePadded = time.length >= 8 ? time : `&nbsp;${time} `;
		this.elem.querySelector('.header .right .time').innerHTML = timePadded;

		// get image offset calculation
		// is slides slightly because of scaling so we have to take a measurement from the rendered page
		const actualFrameHeight = this.elem.querySelector('.frame').scrollHeight;

		// scroll to image
		this.elem.querySelector('.scroll-area').style.top = `${-this.screenIndex * actualFrameHeight}px`;

		this.finishDraw();
	}
}

// register display
registerDisplay(new Radar(11, 'radar'));
